import {
  activeMembership,
  configuredHomeDashboardCards,
  isMembershipCurrentlyActive,
  resolvedLeagueID,
  type SBotMembership,
  type SBotUser,
} from "./user";
import type { ActionAuditScope } from "./actionAudit";

function membershipForLeague(user: SBotUser | null | undefined, leagueID?: string): SBotMembership | undefined {
  if (!user) return undefined;
  const active = activeMembership(user);
  if (!leagueID) return active;
  if (active && isMembershipCurrentlyActive(active) && resolvedLeagueID(active) === leagueID) return active;
  return user.memberships.find(
    (membership) => isMembershipCurrentlyActive(membership) && resolvedLeagueID(membership) === leagueID,
  );
}

/** Mirrors SBotFeatureAccessPolicy.canUseAI. */
export function canUseAI(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID?: string,
): boolean {
  if (isSBotLabsAdmin) return true;
  const membership = membershipForLeague(user, leagueID);
  if (!membership) return true;
  return membership.role !== "leagueStaff"
    && membership.role !== "organizationManager"
    && membership.role !== "organizationStaff";
}

/** League-wide administration exposes colleague-level usage, so only a Labs
 * admin or an active league-target admin for this exact league may inspect it.
 * Organization admins and league managers are intentionally excluded. */
export function canManageLeagueAdministration(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID: string,
): boolean {
  if (isSBotLabsAdmin) return true;
  return user?.memberships.some(
    (membership) => isMembershipCurrentlyActive(membership)
      && membership.targetType === "league"
      && membership.role === "admin"
      && resolvedLeagueID(membership) === leagueID,
  ) === true;
}

/** Broadcast Overlay reads require an explicit/default Home-card grant in the target league. */
export function canReadBroadcastOverlays(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID: string,
): boolean {
  if (isSBotLabsAdmin) return true;
  if (!user) return false;
  return user.memberships.some(
    (membership) => isMembershipCurrentlyActive(membership)
      && resolvedLeagueID(membership) === leagueID
      && configuredHomeDashboardCards(membership).includes("broadcastOverlays"),
  );
}

/** Overlay writes are limited to league management that also has the Home card. */
export function canManageBroadcastOverlays(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID: string,
): boolean {
  if (!canReadBroadcastOverlays(user, isSBotLabsAdmin, leagueID)) return false;
  if (isSBotLabsAdmin) return true;
  return user?.memberships.some(
    (membership) => isMembershipCurrentlyActive(membership)
      && membership.targetType === "league"
      && resolvedLeagueID(membership) === leagueID
      && (membership.role === "admin" || membership.role === "leagueManager"),
  ) === true;
}

/** Mirrors the three query shapes allowed for actionAuditEvents reads. */
export function actionAuditScopes(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID?: string | null,
): ActionAuditScope[] {
  if (!leagueID) return [];

  const activeMemberships = user?.memberships.filter(
    (membership) => isMembershipCurrentlyActive(membership) && resolvedLeagueID(membership) === leagueID,
  ) ?? [];
  const isLeagueAdmin = activeMemberships.some(
    (membership) => membership.targetType === "league" && membership.role === "admin",
  );
  if (isSBotLabsAdmin || isLeagueAdmin) {
    return [{ kind: "league", leagueID }];
  }
  if (!user) return [];

  const scopes: ActionAuditScope[] = [];
  const organizationIDs = [...new Set(activeMemberships.flatMap((membership) => {
    if (membership.targetType !== "organization" || membership.role !== "admin") return [];
    const organizationID = membership.organizationID ?? membership.targetID;
    return organizationID ? [organizationID] : [];
  }))];
  if (organizationIDs.length > 0) {
    scopes.push({ kind: "organizations", leagueID, organizationIDs });
  }
  if (activeMemberships.some((membership) => membership.role === "athlete")) {
    scopes.push({ kind: "athleteSelf", userID: user.id });
  }
  return scopes;
}

export function canReadActionAudit(
  user: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  leagueID?: string | null,
): boolean {
  return actionAuditScopes(user, isSBotLabsAdmin, leagueID).length > 0;
}
